// A running chronicle of what happened, loop by loop (Phase 10's "Event
// history"). This is a presentation record, not world state: it lives under its
// own localStorage key rather than in GameState, so recording an entry never
// perturbs the systems the entry describes, and the log survives a reload the
// same way a save does. Entries are tagged with the loop they belong to, so the
// history screen can show the loop the player is living now and still keep the
// ones that came before.

export type HistoryKind = "world" | "gain" | "battle" | "loss" | "milestone"

export interface HistoryEntry {
  loop: number
  day: number
  text: string
  kind: HistoryKind
  at: number
}

const HISTORY_KEY = "echoesBeforeEternity.history"
const MAX_ENTRIES = 600

function read(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? (parsed as HistoryEntry[]) : []
  } catch {
    return []
  }
}

function write(entries: HistoryEntry[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)))
  } catch {
    // The chronicle is a convenience; never let it break the game.
  }
}

/**
 * Appends one line to the chronicle. A consecutive exact duplicate (same loop,
 * day and text) is dropped, so the state-diffing effects that funnel most
 * entries here cannot double-record a single change when React re-runs them.
 */
export function recordHistory(
  loop: number,
  day: number,
  text: string,
  kind: HistoryKind = "world",
): void {
  const entries = read()
  const last = entries[entries.length - 1]
  if (last && last.loop === loop && last.day === day && last.text === text) return
  entries.push({ loop, day, text, kind, at: Date.now() })
  write(entries)
}

/** Appends several lines at once (e.g. a day's worth of reports from the digest). */
export function recordHistoryMany(
  loop: number,
  day: number,
  items: Array<{ text: string; kind?: HistoryKind }>,
): void {
  for (const item of items) recordHistory(loop, day, item.text, item.kind ?? "world")
}

export function getHistory(): HistoryEntry[] {
  return read()
}

export function getHistoryForLoop(loop: number): HistoryEntry[] {
  return read().filter((entry) => entry.loop === loop)
}

/** Which loop numbers have any recorded history, newest first. */
export function getRecordedLoops(): number[] {
  const loops = new Set(read().map((entry) => entry.loop))
  return [...loops].sort((a, b) => b - a)
}

export function clearHistory(): void {
  try {
    localStorage.removeItem(HISTORY_KEY)
  } catch {
    // ignore
  }
}
